import { useRef, useState, useCallback, useEffect, type ReactNode } from 'react'
import styles from './SwipeableRow.module.css'

export interface SwipeableRowProps {
  children: ReactNode
  /** Called when swiped left past the delete threshold or delete button tapped */
  onDelete?: () => void
  /** Called when swiped right past the action threshold or action button tapped */
  onAction?: () => void
  /** Label for the right-side action (swipe left reveals this). Default: "Delete" */
  deleteLabel?: string
  /** Label for the left-side action (swipe right reveals this). Default: "Read" */
  actionLabel?: string
  /** Whether the left-side (swipe right) action is available */
  showAction?: boolean
  /** Disable swipe gestures */
  disabled?: boolean
  className?: string
}

/** Distance to reveal the action buttons */
const REVEAL_THRESHOLD = 80
/** Distance to auto-trigger the action */
const TRIGGER_THRESHOLD = 160
/** Minimum horizontal distance before we start tracking */
const MIN_SWIPE = 10

export function SwipeableRow({
  children,
  onDelete,
  onAction,
  deleteLabel = 'Delete',
  actionLabel = 'Read',
  showAction = true,
  disabled = false,
  className,
}: SwipeableRowProps) {
  const rowRef = useRef<HTMLDivElement>(null)
  const [isTouchDevice, setIsTouchDevice] = useState(false)

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0)
  }, [])

  const startX = useRef(0)
  const startY = useRef(0)
  const currentX = useRef(0)
  const isTracking = useRef(false)
  const direction = useRef<'left' | 'right' | null>(null)
  const [offset, setOffset] = useState(0)
  const [transitioning, setTransitioning] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  const reset = useCallback(() => {
    setTransitioning(true)
    setOffset(0)
    direction.current = null
    setTimeout(() => setTransitioning(false), 200)
  }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (disabled || dismissed || !isTouchDevice) return
    const touch = e.touches[0]
    startX.current = touch.clientX
    startY.current = touch.clientY
    currentX.current = touch.clientX
    isTracking.current = false
    direction.current = null
    setTransitioning(false)
  }, [disabled, dismissed, isTouchDevice])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (disabled || dismissed || !isTouchDevice) return
    const touch = e.touches[0]
    const deltaX = touch.clientX - startX.current
    const deltaY = touch.clientY - startY.current

    // Determine if this is a horizontal swipe (ignore vertical scrolling)
    if (!isTracking.current) {
      if (Math.abs(deltaX) < MIN_SWIPE) return
      // If more vertical than horizontal, don't track
      if (Math.abs(deltaY) > Math.abs(deltaX)) return
      isTracking.current = true
      direction.current = deltaX < 0 ? 'left' : 'right'
    }

    /* v8 ignore next -- ref is always attached while the component is mounted */
    if (!isTracking.current) return

    currentX.current = touch.clientX
    let newOffset = deltaX

    // Don't allow right swipe if no action
    if (newOffset > 0 && !showAction) {
      newOffset = 0
    }

    // Apply resistance past the trigger threshold
    const absOffset = Math.abs(newOffset)
    if (absOffset > TRIGGER_THRESHOLD) {
      const excess = absOffset - TRIGGER_THRESHOLD
      const dampened = TRIGGER_THRESHOLD + excess * 0.3
      newOffset = newOffset < 0 ? -dampened : dampened
    }

    setOffset(newOffset)
  }, [disabled, dismissed, showAction, isTouchDevice])

  const handleTouchEnd = useCallback(() => {
    if (!isTracking.current || disabled || dismissed || !isTouchDevice) return
    isTracking.current = false

    const absOffset = Math.abs(offset)

    // Full swipe — trigger action
    if (absOffset >= TRIGGER_THRESHOLD) {
      if (offset < 0 && onDelete) {
        // Swipe left far → delete
        setTransitioning(true)
        /* v8 ignore next -- layout fallback; offsetWidth/Height is numeric once mounted in a real browser */
        setOffset(-(rowRef.current?.offsetWidth ?? 400))
        setTimeout(() => {
          setDismissed(true)
          onDelete()
        }, 200)
        return
      }
      if (offset > 0 && onAction && showAction) {
        // Swipe right far → action (mark read)
        setTransitioning(true)
        /* v8 ignore next -- layout fallback; offsetWidth/Height is numeric once mounted in a real browser */
        setOffset(rowRef.current?.offsetWidth ?? 400)
        setTimeout(() => {
          onAction()
          reset()
          setDismissed(false)
        }, 200)
        return
      }
    }

    // Partial swipe — snap to reveal buttons or reset
    if (absOffset >= REVEAL_THRESHOLD / 2) {
      setTransitioning(true)
      const snapTo = offset < 0 ? -REVEAL_THRESHOLD : REVEAL_THRESHOLD
      setOffset(snapTo)
      setTimeout(() => setTransitioning(false), 200)
    } else {
      reset()
    }
  }, [offset, onDelete, onAction, showAction, disabled, dismissed, isTouchDevice, reset])

  if (dismissed) return null

  const leftRevealed = offset > 0
  const rightRevealed = offset < 0

  return (
    <div
      className={[styles.wrapper, className].filter(Boolean).join(' ')}
      ref={rowRef}
    >
      {/* Left action panel (swipe right reveals — mark read) */}
      {showAction && (
        <div className={`${styles.actionPanel} ${styles.actionLeft} ${leftRevealed ? styles.actionPanelVisible : ''}`}>
          <button
            className={styles.actionBtn}
            onClick={() => { onAction?.(); reset() }}
          >
            <span className={styles.actionLabel}>{actionLabel}</span>
          </button>
          {leftRevealed && Math.abs(offset) >= TRIGGER_THRESHOLD && (
            <span className={styles.triggerHint}>Release</span>
          )}
        </div>
      )}

      {/* Right action panel (swipe left reveals — delete) */}
      <div className={`${styles.actionPanel} ${styles.actionRight} ${rightRevealed ? styles.actionPanelVisible : ''}`}>
        <button
          className={`${styles.actionBtn} ${styles.deleteBtn}`}
          onClick={() => {
            setTransitioning(true)
            /* v8 ignore next -- layout fallback; offsetWidth/Height is numeric once mounted in a real browser */
            setOffset(-(rowRef.current?.offsetWidth ?? 400))
            setTimeout(() => {
              setDismissed(true)
              onDelete?.()
            }, 200)
          }}
        >
          <span className={styles.actionLabel}>{deleteLabel}</span>
        </button>
        {rightRevealed && Math.abs(offset) >= TRIGGER_THRESHOLD && (
          <span className={styles.triggerHint}>Release</span>
        )}
      </div>

      {/* Main content */}
      <div
        className={styles.content}
        style={{
          transform: `translateX(${offset}px)`,
          transition: transitioning ? 'transform 200ms ease-out' : 'none',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
    </div>
  )
}
